// Hoàn tất thông tin tài khoản — bắt buộc với mọi đơn vị (Shop, CTV, Đại lý) trước
// khi dùng hệ thống. Ba bước, mỗi bước lưu riêng:
//   1. Thông tin chủ tài khoản: họ tên, SĐT (số đăng nhập, chỉ xem), email.
//   2. Kho lấy hàng: ít nhất một kho đủ người liên hệ, SĐT, địa chỉ, tỉnh, phường/xã.
//   3. Tài khoản ngân hàng nhận tiền: số tài khoản + chủ tài khoản.
// Trạng thái lấy từ GET /onboarding/required (cùng khoá với màn chặn ProfileGate).
// ?step=owner|warehouse|bank mở thẳng bước đó.

import { useEffect, useRef, useState } from 'react'
import { Navigate, useNavigate, useSearchParams } from 'react-router-dom'
import { useMutation, useQuery } from '@tanstack/react-query'
import { toast } from 'sonner'
import { Check, ChevronDown, LogOut } from 'lucide-react'
import { ApiError } from '@/lib/api'
import { logout, updateOwnerProfile } from '@/lib/auth'
import { formatPhoneVN } from '@/lib/format'
import { getHomeRouteForTenant, getRoleTheme } from '@/lib/permissions'
import {
  fetchProfileStatus,
  isAdminTenant,
  profileQueryKey,
  type ProfileItem,
  type ProfileStatus,
  type ProfileStepKey,
} from '@/lib/onboarding'
import { cn } from '@/lib/utils'
import { useAuthState } from '@/hooks/useAuthState'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { ShopTrackLogo } from '@/components/ui/logo'
import { LoadError } from '@/components/ui/data-notice'
import { Spinner } from '@/components/ui/spinner'
import { FieldRow } from '@/components/settings/FieldRow'
import { WarehouseManager } from '@/components/settings/WarehouseManager'
import { BankAccountsManager } from '@/components/settings/BankAccounts'

const STEP_KEYS: ProfileStepKey[] = ['owner', 'warehouse', 'bank']
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/

/** Vai trò chỉ xem trong đơn vị — không sửa được kho / ngân hàng (server: owner | manager). */
const READ_ONLY_ROLES = ['staff', 'warehouse']

const asStep = (v: string | null): ProfileStepKey | null =>
  v && (STEP_KEYS as string[]).includes(v) ? (v as ProfileStepKey) : null

export default function OnboardingPage() {
  const auth = useAuthState()
  const nav = useNavigate()
  const [params, setParams] = useSearchParams()
  const shop = auth.shops.find((s) => s.shopId === auth.activeShopId)
  const role = shop?.role
  const tenantType = shop?.tenantType || (role === 'agency' || role === 'ctv' ? role : 'shop')
  const home = getHomeRouteForTenant(shop?.tenantType, shop?.role, shop?.staff, shop?.workModes)
  const applies = !!auth.activeShopId && !!shop && !isAdminTenant(shop.tenantType)
  const roleTheme = getRoleTheme(tenantType, role)

  const q = useQuery({
    queryKey: profileQueryKey(auth.activeShopId),
    queryFn: fetchProfileStatus,
    enabled: applies,
    staleTime: 0,
  })
  const items = q.data?.items ?? []
  const doneCount = items.filter((i) => i.done).length

  // Bước đang mở: theo ?step=, không có thì bước đầu tiên chưa xong.
  const paramStep = asStep(params.get('step'))
  const [open, setOpen] = useState<ProfileStepKey | null>(paramStep)
  const openRef = useRef(open)
  openRef.current = open
  // Bước cần cuộn tới sau lần render kế tiếp (mở từ link ?step= hoặc tự chuyển bước).
  const scrollTo = useRef<ProfileStepKey | null>(paramStep)
  const sectionRefs = useRef<Partial<Record<ProfileStepKey, HTMLElement | null>>>({})

  // Đổi ?step= từ bên ngoài (VD: nút "Hoàn tất thông tin" trên thông báo lỗi) → mở bước đó.
  useEffect(() => {
    if (!paramStep || paramStep === openRef.current) return
    scrollTo.current = paramStep
    setOpen(paramStep)
  }, [paramStep])

  // Lần đầu có dữ liệu mà URL không chỉ bước nào → mở bước đầu tiên chưa xong.
  const initialized = useRef(!!paramStep)
  // Một bước vừa chuyển sang "xong" → tự mở bước kế tiếp còn thiếu.
  const prevDone = useRef<Record<string, boolean> | null>(null)
  useEffect(() => {
    if (!q.data) return
    const list = q.data.items
    const now = Object.fromEntries(list.map((i) => [i.key, i.done]))
    const prev = prevDone.current
    prevDone.current = now
    if (!initialized.current) {
      initialized.current = true
      setOpen(list.find((i) => !i.done)?.key ?? null)
      return
    }
    if (!prev) return
    const justDone = list.find((i) => i.done && !prev[i.key])
    if (justDone && openRef.current === justDone.key) {
      const next = list.find((i) => !i.done)?.key ?? null
      scrollTo.current = next
      setOpen(next)
      // Xong cả ba bước → đưa lên đầu trang, nơi có nút "Bắt đầu sử dụng".
      if (!next) window.scrollTo({ top: 0, behavior: 'smooth' })
    }
  }, [q.data])

  // Giữ ?step= theo bước đang mở để tải lại trang vẫn đúng chỗ.
  useEffect(() => {
    if ((open ?? null) === paramStep) return
    setParams(open ? { step: open } : {}, { replace: true })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open])

  useEffect(() => {
    const key = scrollTo.current
    if (!key || key !== open || !q.data) return
    scrollTo.current = null
    sectionRefs.current[key]?.scrollIntoView({ behavior: 'smooth', block: 'start' })
  }, [open, q.data])

  useEffect(() => {
    document.documentElement.setAttribute('data-role-theme', roleTheme)
    return () => document.documentElement.removeAttribute('data-role-theme')
  }, [roleTheme])

  if (!auth.isAuthenticated) return <Navigate to="/login" replace />
  // Đơn vị quản trị / phiên chưa chọn shop không có hồ sơ bắt buộc.
  if (!applies) return <Navigate to={home} replace />
  // Nhân viên (docs/KE_HOACH_THANH_VIEN_DON_VI.md): hồ sơ đơn vị là việc của chủ — màn chặn (ProfileGate) đã báo
  if (role === 'staff') return <Navigate to={home} replace />

  const canManage = !READ_ONLY_ROLES.includes(role ?? '')
  const canEditOwner = canManage && role !== 'manager'

  async function onLogout() {
    await logout()
    nav('/login', { replace: true })
  }

  return (
    <div data-role-theme={roleTheme} className="min-h-dvh bg-paper">
      <header className="sticky top-0 z-30 border-b border-rule bg-raise">
        <div className="mx-auto flex h-14 max-w-3xl items-center justify-between gap-3 px-4 sm:px-6">
          <div className="flex min-w-0 items-center gap-3">
            <ShopTrackLogo className="text-accent" showText={false} />
            <span className="truncate text-sm font-medium text-ink" title={shop?.name}>{shop?.name}</span>
          </div>
          <button
            type="button"
            onClick={onLogout}
            className="motion-state -mr-2 inline-flex min-h-11 shrink-0 items-center gap-1.5 rounded-lg px-2 text-sm text-muted hover:text-danger desktop:min-h-9"
          >
            <LogOut className="size-4" aria-hidden />
            Đăng xuất
          </button>
        </div>
      </header>

      <main className="app-content mx-auto max-w-3xl space-y-6 px-4 pb-16 pt-6 sm:px-6 sm:pt-8">
        <div className="space-y-2">
          <h1>Hoàn tất thông tin tài khoản</h1>
        </div>

        {q.isPending ? (
          <div className="flex items-center justify-center gap-2 py-16 text-sm text-muted">
            <Spinner /> Đang tải trạng thái…
          </div>
        ) : q.isError ? (
          <div className="rounded-xl bg-raise shadow-card">
            <LoadError onRetry={() => q.refetch()}>Không tải được trạng thái hồ sơ.</LoadError>
          </div>
        ) : (
          <>
            {q.data.complete ? (
              <section className="flex flex-col gap-4 rounded-xl bg-raise p-5 shadow-card sm:flex-row sm:items-center sm:justify-between dark:border dark:border-rule dark:shadow-none">
                <div className="flex items-start gap-3">
                  <span className="grid size-10 shrink-0 place-items-center rounded-full bg-ok/12 text-ok">
                    <Check className="size-5" aria-hidden />
                  </span>
                  <div>
                    <h2>Đã đủ thông tin</h2>
                  </div>
                </div>
                <Button className="shrink-0" onClick={() => nav(home, { replace: true })}>
                  Bắt đầu sử dụng
                </Button>
              </section>
            ) : (
              <div className="space-y-2" aria-live="polite">
                <p className="text-sm text-inkSoft">
                  Đã xong <b className="font-semibold text-ink tabular-nums">{doneCount}/{items.length}</b> bước
                </p>
                <div className="flex gap-1.5" role="img" aria-label={`Đã xong ${doneCount} trên ${items.length} bước`}>
                  {items.map((i) => (
                    <span key={i.key} className={cn('h-1.5 flex-1 rounded-full', i.done ? 'bg-ok' : 'bg-rule')} />
                  ))}
                </div>
              </div>
            )}

            <ol className="space-y-3">
              {items.map((item, idx) => (
                <li key={item.key} ref={(el) => { sectionRefs.current[item.key] = el }} className="scroll-mt-20">
                  <StepCard
                    index={idx + 1}
                    item={item}
                    open={open === item.key}
                    onToggle={() => setOpen((cur) => (cur === item.key ? null : item.key))}
                  >
                    {item.key === 'owner' && <OwnerStep item={item} canEdit={canEditOwner} owner={q.data?.owner ?? null} />}
                    {item.key === 'warehouse' && (
                      <StepBody readOnly={!canManage}>
                        <WarehouseManager intro={false} readOnly={!canManage} />
                      </StepBody>
                    )}
                    {item.key === 'bank' && (
                      <StepBody readOnly={!canManage}>
                        <BankAccountsManager intro={false} readOnly={!canManage} />
                      </StepBody>
                    )}
                  </StepCard>
                </li>
              ))}
            </ol>
          </>
        )}
      </main>
    </div>
  )
}

function StepCard({
  index,
  item,
  open,
  onToggle,
  children,
}: {
  index: number
  item: ProfileItem
  open: boolean
  onToggle: () => void
  children: React.ReactNode
}) {
  const bodyId = `onboarding-step-${item.key}`
  return (
    <section className={cn('rounded-xl bg-raise shadow-card dark:border dark:border-rule dark:shadow-none', open && !item.done && 'ring-1 ring-accent/30')}>
      <button
        type="button"
        onClick={onToggle}
        aria-expanded={open}
        aria-controls={bodyId}
        className="motion-state flex w-full items-center gap-3 rounded-xl px-4 py-4 text-left hover:bg-paperSoft/50 sm:px-5"
      >
        {item.done ? (
          <span className="grid size-8 shrink-0 place-items-center rounded-full bg-ok/12 text-ok" aria-hidden>
            <Check className="size-4" />
          </span>
        ) : (
          <span className="grid size-8 shrink-0 place-items-center rounded-full border border-rule text-sm font-semibold text-inkSoft tabular-nums" aria-hidden>
            {index}
          </span>
        )}
        <span className="min-w-0 flex-1">
          <span className="block text-sm font-semibold text-ink">{item.label}</span>
          <span className={cn('block text-xs', item.done ? 'text-ok' : 'text-warn')}>
            {item.done ? 'Đã xong' : `Còn thiếu: ${item.missing.join(', ') || 'chưa khai'}`}
          </span>
        </span>
        <ChevronDown className={cn('size-4 shrink-0 text-muted transition-transform', open && 'rotate-180')} aria-hidden />
      </button>
      {open && (
        <div id={bodyId} className="border-t border-rule px-4 py-4 sm:px-5 sm:py-5">
          {children}
        </div>
      )}
    </section>
  )
}

function StepBody({ readOnly, children }: { readOnly: boolean; children: React.ReactNode }) {
  return (
    <div className="space-y-4">
      {readOnly && <OwnerOnlyNotice />}
      {children}
    </div>
  )
}

function OwnerOnlyNotice() {
  return (
    <p className="rounded-lg bg-warn/10 px-3 py-2 text-sm text-ink">
      Tài khoản của bạn không có quyền cập nhật phần này.
    </p>
  )
}

function OwnerStep({ item, canEdit, owner }: { item: ProfileItem; canEdit: boolean; owner: ProfileStatus['owner'] }) {
  const auth = useAuthState()
  const [editing, setEditing] = useState(!item.done)
  const [name, setName] = useState(owner?.name ?? auth.name ?? '')
  const [email, setEmail] = useState(owner?.email ?? auth.email ?? '')
  const [submitted, setSubmitted] = useState(false)
  const [emailTaken, setEmailTaken] = useState<string | null>(null)
  const phoneMissing = item.missing.includes('số điện thoại')

  const nameError = !name.trim() ? 'Nhập họ và tên chủ tài khoản' : null
  const emailError = emailTaken ?? (!email.trim() ? 'Nhập email' : !EMAIL_RE.test(email.trim()) ? 'Email không đúng định dạng' : null)

  const save = useMutation({
    mutationFn: () => updateOwnerProfile({ name: name.trim(), email: email.trim().toLowerCase() }),
    onSuccess: () => {
      toast.success('Đã lưu thông tin chủ tài khoản')
      setEditing(false)
      setSubmitted(false)
    },
    onError: (e) => {
      if (e instanceof ApiError && e.code === 'auth.email_taken') setEmailTaken(e.message || 'Email này đã được dùng cho tài khoản khác')
      else toast.error((e as Error).message)
    },
  })

  const phoneRow = (
    <FieldRow label="Số điện thoại đăng nhập">
      <div className="flex h-11 items-center rounded-md border border-rule bg-paperSoft px-3.5 text-sm text-inkSoft tabular-nums">
        {auth.phone ? formatPhoneVN(auth.phone) : 'Chưa có'}
      </div>
      {phoneMissing && (
        <p className="text-xs text-warn">Tài khoản chưa có số điện thoại đăng nhập.</p>
      )}
    </FieldRow>
  )

  if (!canEdit) {
    return (
      <div className="space-y-4">
        <OwnerOnlyNotice />
      </div>
    )
  }

  if (!editing) {
    return (
      <div className="space-y-4">
        <dl className="grid grid-cols-1 gap-3 text-sm sm:grid-cols-3">
          <div>
            <dt className="text-xs text-muted">Họ và tên</dt>
            <dd className="text-ink">{auth.name || 'Đã khai'}</dd>
          </div>
          <div>
            <dt className="text-xs text-muted">Số điện thoại</dt>
            <dd className="text-ink tabular-nums">{auth.phone ? formatPhoneVN(auth.phone) : '—'}</dd>
          </div>
          <div className="min-w-0">
            <dt className="text-xs text-muted">Email</dt>
            <dd className="truncate text-ink" title={auth.email ?? undefined}>{auth.email || 'Đã khai'}</dd>
          </div>
        </dl>
        <Button variant="outline" size="sm" onClick={() => { setName(owner?.name ?? auth.name ?? ''); setEmail(owner?.email ?? auth.email ?? ''); setEditing(true) }}>
          Sửa
        </Button>
      </div>
    )
  }

  return (
    <form
      noValidate
      onSubmit={(e) => {
        e.preventDefault()
        setSubmitted(true)
        if (nameError || emailError) return
        save.mutate()
      }}
      className="space-y-4"
    >
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <FieldRow label="Họ và tên" error={submitted ? nameError : null} className="sm:col-span-2">
          <Input
            value={name}
            onChange={(e) => setName(e.target.value)}
            maxLength={100}
            autoComplete="name"
            placeholder="VD: Nguyễn Mai Anh"
            aria-invalid={submitted && nameError ? true : undefined}
            disabled={save.isPending}
          />
        </FieldRow>
        {phoneRow}
        <FieldRow label="Email" error={submitted || emailTaken ? emailError : null}>
          <Input
            type="email"
            inputMode="email"
            value={email}
            onChange={(e) => { setEmail(e.target.value); setEmailTaken(null) }}
            maxLength={254}
            autoComplete="email"
            placeholder="ten@vidu.com"
            aria-invalid={(submitted || emailTaken) && emailError ? true : undefined}
            disabled={save.isPending}
          />
        </FieldRow>
      </div>
      <div className="flex items-center gap-2">
        <Button type="submit" disabled={save.isPending}>
          {save.isPending ? 'Đang lưu…' : 'Lưu'}
        </Button>
        {item.done && (
          <Button type="button" variant="ghost" onClick={() => setEditing(false)} disabled={save.isPending}>
            Hủy
          </Button>
        )}
      </div>
    </form>
  )
}
